import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import type {SavedViewsResponse} from '@github-ui/saved-views'
import {fetchSavedViews} from '@github-ui/saved-views/queries'

import {repoAppBuilder} from '@github-ui/repo-app-builder'

import {shouldRevalidateSavedView} from '../utils/saved-view-url-params'
import {
  adaptSavedViewRoutePayload,
  SAVED_VIEW_ROUTE_ID,
  savedViewContentPath,
  type SavedViewRoutePayload,
  savedViewRouteSearchParams,
} from './saved-view-route-payload'

export function buildSavedViewsRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('savedViewsRoute', {
    path: '/:owner/:repo/issues/views',
    queries: [
      // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The existing views.json endpoint returns plain JSON, not a Protobuf payload.
      mainQuery<SavedViewsResponse>({
        queryDeps: ({pathname}) => {
          const [, owner, repo] = pathname.split('/')
          return {pathname: `/${owner}/${repo}/issues/views.json`}
        },
        queryFn: async ({queryDeps}, {signal}) => {
          const [, owner = '', repo = ''] = queryDeps.pathname.split('/')
          const payload = await fetchSavedViews({owner, repo, signal})
          return {meta: {}, payload} as unknown as SavedViewsResponse
        },
      }),
    ],
  })
}

export function buildSavedViewNewRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('savedViewNewRoute', {
    path: '/:owner/:repo/issues/views/new',
    queries: [],
  })
}

/** Served as the list, the page reads the view itself and shows that it is missing. */
const VIEW_NOT_FOUND = {meta: {}, payload: {boardEnabled: false}} as unknown as SavedViewRoutePayload

// The server decides the layout, so the page asks it on every navigation. The list results load
// through their own query, shared with the issues index.
export function buildSavedViewRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The content endpoint returns plain JSON validated by `adaptSavedViewRoutePayload`, not a Protobuf payload.
  const contentQuery = mainQuery<SavedViewRoutePayload>({
    adapt: adaptSavedViewRoutePayload,
    queryDeps: ({pathname, searchParams}) => ({
      pathname: savedViewContentPath(pathname),
      searchParams: savedViewRouteSearchParams(searchParams),
    }),
  })

  return appBuilder.createQueryRouteConfig(SAVED_VIEW_ROUTE_ID, {
    path: '/:owner/:repo/issues/views/:id',
    shouldRevalidate: shouldRevalidateSavedView,
    uiServiceOptions: {
      tsrConfig: {
        // TanStack Router reloads only when `queryDeps` change, and they leave out visible fields.
        shouldReload: undefined,
      },
    },
    queries: [
      {
        ...contentQuery,
        // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- Wraps the `contentQuery` fetch, which reads plain JSON.
        queryFn: async (queryKey, options) => {
          try {
            return await contentQuery.queryFn(queryKey, options)
          } catch (error) {
            // `/:id.json` 404s for a missing view too, and `RepositorySavedViewContent` shows that with
            // a link back to the views. Thrown, it would reach the generic route error page instead.
            if (isResponseError(error) && error.response.status === 404) return VIEW_NOT_FOUND
            throw error
          }
        },
      },
    ],
  })
}

export const savedViewsRoute = buildSavedViewsRoute(repoAppBuilder)
export const savedViewNewRoute = buildSavedViewNewRoute(repoAppBuilder)
export const savedViewRoute = buildSavedViewRoute(repoAppBuilder)
